import {Component, OnInit} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {Invoice} from '../../model/invoice';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {InvoicePeerService} from '../../service/invoice-peer.service';
import {EmailLogsResource, EmailTemplatesResource, InvoiceAttachmentsResource} from '../../model/model-resource';
import { MatSelectChange } from '@angular/material/select';
import {map, switchMap, tap} from 'rxjs/operators';
import {ObjectQueryFilterOperator, ObjectQueryParameters, ObjectQuerySortDirection} from '../../model/object-query-parameters';
import {EmailLog} from '../../model/email-log';
import {EmailTemplate} from '../../model/email-template';
import {InvoiceAttachment} from '../../model/invoice-attachment';
import {EmailStatesMapCache, InvoiceAttachmentTypesMapCache, UsersMapCache} from '../../model/model-map-cache';

@Component({
  selector: 'app-invoice-email-widget',
  templateUrl: './invoice-email-widget.component.html',
  styleUrls: ['./invoice-email-widget.component.scss']
})
export class InvoiceEmailWidgetComponent implements OnInit {

  data$;

  invoice: Invoice;
  selectedItem: EmailLog;

  form: UntypedFormGroup;
  locked = false;
  added = false;
  saving = false;

  constructor(
    private invoicePeerService: InvoicePeerService,
    private emailLogsResource: EmailLogsResource,
    private emailTemplatesResource: EmailTemplatesResource,
    private invoiceAttachmentsResource: InvoiceAttachmentsResource,
    private emailStatesMapCache: EmailStatesMapCache,
    private usersMapCache: UsersMapCache,
    private invoiceAttachmentTypesMapCache: InvoiceAttachmentTypesMapCache,
    private builder: UntypedFormBuilder) {
    this.form = this.builder.group({
      emailTemplateId: [null],
      recipient: [null, Validators.required],
      subject: [null, Validators.required],
      message: [null, Validators.required],
      invoiceAttachmentIds: [[]]
    });
  }

  add(): void {
    this.invoicePeerService.lock();
    this.added = true;
    this.form.reset();
  }

  revert(): void {
    this.invoicePeerService.unlock();
    this.added = false;
  }

  save(): void {
    this.saving = true;
    const emailLog = new EmailLog();
    emailLog.invoiceId = this.invoice.invoiceId;
    emailLog.recipient = this.form.value.recipient;
    emailLog.subject = this.form.value.subject;
    emailLog.message = this.form.value.message;
    emailLog.invoiceAttachmentIds = this.form.value.invoiceAttachmentIds;
    this.emailLogsResource.save(emailLog).subscribe(() => {
      this.invoicePeerService.unlock();
      this.invoicePeerService.refresh();
      this.selectedItem = null;
      this.saving = false;
      this.added = false;
    });
  }

  itemClicked(item) {
    this.selectedItem = (item !== this.selectedItem ? item : null);
  }

  templateChange(event: MatSelectChange) {
    this.form.patchValue({
      message: event.value.message,
      subject: event.value.subject
    });
  }

  ngOnInit() {

    this.data$ = this.invoicePeerService
      .get()
      .pipe(
        tap(invoice => this.invoice = invoice),
        switchMap((invoice) => combineLatest([
          this.emailStatesMapCache.map$,
          this.usersMapCache.map$,
          this.invoiceAttachmentTypesMapCache.map$,
          this.getEmailTemplates(),
          this.getEmailLogs(invoice.invoiceId),
          this.getInvoiceAttachments(invoice.invoiceId)
        ])),
        map(([emailStates, users, invoiceAttachmentTypes, emailTemplates, emailLogs, invoiceAttachments]) => ({
          emailStates,
          users,
          invoiceAttachmentTypes,
          emailTemplates,
          emailLogs,
          invoiceAttachments
        })));


    this.invoicePeerService.getState().subscribe((state) => {
      if (state === 'locked') {
        this.locked = true;
      } else if (state === 'unlocked') {
        this.locked = false;
      }
    });
  }

  private getEmailLogs(invoiceId: number): Observable<EmailLog[]> {
    const params = new ObjectQueryParameters();
    params.addFilter('invoiceId', ObjectQueryFilterOperator.Equals, invoiceId.toString());
    params.addSort('created', ObjectQuerySortDirection.Descending);
    return this.emailLogsResource
      .query(params);
  }

  private getEmailTemplates(): Observable<EmailTemplate[]> {
    const params = new ObjectQueryParameters();
    params.addSort('name', ObjectQuerySortDirection.Ascending);
    return this.emailTemplatesResource
      .query(params);
  }

  private getInvoiceAttachments(invoiceId: number): Observable<InvoiceAttachment[]> {
    const params = new ObjectQueryParameters();
    params.addFilter('invoiceId', ObjectQueryFilterOperator.Equals, invoiceId.toString());
    params.addSort('created', ObjectQuerySortDirection.Descending);
    return this.invoiceAttachmentsResource
      .query(params);
  }
}
